import React, { useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { Plus, TrendingUp, Crown, History } from 'lucide-react';
import { PageHeader, KpiCard, StatusChip, Panel, ProgressRow, FormDialog, LoadGate, PageShell, CHART_FONT } from './businessUi';
import { formatCurrency } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';
import { StatusDialog, StatusHistoryDialog, withStatusChange } from './statusHistory';

const KEYS = { orders: ERP_KEYS.sales };
const statusTone = { Paid: 'success', Invoiced: 'default', Dispatched: 'default', Confirmed: 'warning', Draft: 'secondary' };
const PIPELINE = ['Draft', 'Confirmed', 'Dispatched', 'Invoiced', 'Paid'];
const pipeTone = { Draft: 'secondary', Confirmed: 'warning', Dispatched: 'sky', Invoiced: 'primary', Paid: 'success' };

const ADD_FIELDS = [
  { name: 'customer', label: 'Customer', required: true },
  { name: 'date', label: 'Order Date', type: 'date', default: '2026-06-13' },
  { name: 'items', label: 'Line Items', type: 'number', default: 1 },
  { name: 'amount', label: 'Order Value (INR)', type: 'number', default: 0 },
  { name: 'status', label: 'Status', type: 'select', default: 'Draft', options: ['Draft', 'Confirmed', 'Dispatched', 'Invoiced', 'Paid'] },
];

export default function Sales() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusIndex, setStatusIndex] = useState(null);
  const [historyId, setHistoryId] = useState(null);

  const salesOrders = data?.orders || [];
  const handleAdd = (v) => {
    const next = [{ id: `SO-${5500 + salesOrders.length + 1}`, ...v }, ...salesOrders];
    save('orders', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  // Move an order along its pipeline with a date; status follows, history appended.
  const handleStatusUpdate = (v) => {
    if (statusIndex === null) return;
    const next = salesOrders.map((o, i) => (i === statusIndex ? withStatusChange(o, v, 'status') : o));
    save('orders', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  const historyOrder = salesOrders.find((o) => o.id === historyId) || null;

  const total = salesOrders.reduce((s, o) => s + o.amount, 0);
  const paid = salesOrders.filter((o) => o.status === 'Paid').reduce((s, o) => s + o.amount, 0);
  const open = salesOrders.filter((o) => o.status !== 'Paid').length;

  const sorted = [...salesOrders].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const trendOptions = {
    chart: { type: 'area', toolbar: { show: false }, fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 6, blur: 6, opacity: 0.12, color: '#7c5cff' } },
    stroke: { curve: 'smooth', width: 3 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 } },
    colors: ['#7c5cff'],
    dataLabels: { enabled: false },
    xaxis: { categories: sorted.map((o) => String(o.date).slice(5)), labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { formatter: (v) => `${Math.round(v / 1000)}k`, style: { fontSize: '11px' } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: 'background.default', strokeDashArray: 4 },
    markers: { size: 4, colors: ['#7c5cff'], strokeWidth: 0 },
  };
  const trendSeries = [{ name: 'Order Value', data: sorted.map((o) => o.amount) }];

  const pipelineCounts = PIPELINE.map((s) => ({ status: s, count: salesOrders.filter((o) => o.status === s).length }));
  const maxPipe = Math.max(1, ...pipelineCounts.map((p) => p.count));

  const byCustomer = {};
  salesOrders.forEach((o) => { byCustomer[o.customer] = (byCustomer[o.customer] || 0) + o.amount; });
  const topCustomers = Object.entries(byCustomer).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount).slice(0, 5);
  const maxCust = Math.max(1, ...topCustomers.map((c) => c.amount));

  return (
    <PageShell>
      <PageHeader
        title="Sales"
        subtitle="Sales orders, dispatch & invoicing"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New Order</Button>}
      />

      <FormDialog open={dialogOpen} title="New Sales Order" fields={ADD_FIELDS} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Create Order" />

      <StatusDialog
        open={statusIndex !== null}
        record={statusIndex !== null ? salesOrders[statusIndex] : null}
        label="Status"
        statusField="status"
        options={PIPELINE}
        title={statusIndex !== null ? `Update Status · ${salesOrders[statusIndex].id}` : 'Update Status'}
        onClose={() => setStatusIndex(null)}
        onSubmit={handleStatusUpdate}
      />

      <StatusHistoryDialog
        open={historyId !== null}
        record={historyOrder}
        statusField="status"
        toneFor={(s) => statusTone[s]}
        title={historyOrder ? `${historyOrder.customer} · ${historyOrder.id}` : 'Status History'}
        subtitle="Order status history"
        onClose={() => setHistoryId(null)}
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="Order Value" value={formatCurrency(total)} icon={TrendingUp} tone="primary" />
          <KpiCard label="Collected" value={formatCurrency(paid)} icon={TrendingUp} tone="success" hint={total ? `${Math.round((paid / total) * 100)}% of pipeline` : ''} />
          <KpiCard label="Open Orders" value={String(open)} icon={TrendingUp} tone="warning" />
        </Box>

        {salesOrders.length === 0 ? (
          <Panel title="Sales Orders" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No sales orders yet. Click <strong>New Order</strong> to create one — it’s saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <>
            <Panel title="Revenue Trend" subtitle="Order value over time" accent="linear-gradient(90deg,#7c5cff,#818cf8)" sx={{ mb: 2.5 }}>
              <Box sx={{ px: 2, pb: 1 }}>
                <ReactApexChart options={trendOptions} series={trendSeries} type="area" height={260} />
              </Box>
            </Panel>

            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 2.5 }}>
              <Panel title="Order Pipeline" subtitle="Orders by stage" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
                <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                  {pipelineCounts.map((p) => (
                    <ProgressRow key={p.status} label={p.status} value={p.count} max={maxPipe} tone={pipeTone[p.status]} valueLabel={`${p.count} order${p.count === 1 ? '' : 's'}`} />
                  ))}
                </Box>
              </Panel>

              <Panel title="Top Customers" subtitle="By order value" accent="linear-gradient(90deg,#f59e0b,#fbbf24)">
                <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
                  {topCustomers.map((c, i) => (
                    <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: i < topCustomers.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: i === 0 ? '#b45309' : '#475569', bgcolor: i === 0 ? 'rgba(245,158,11,0.16)' : 'rgba(100,116,139,0.12)' }}>
                        {i === 0 ? <Crown size={14} /> : i + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>{c.name}</Typography>
                        <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'background.default', mt: 0.5, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(c.amount / maxCust) * 100}%`, background: '#7c5cff', borderRadius: 3 }} />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(c.amount)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Panel>
            </Box>

            <Panel title="Recent Orders" subtitle="Click a row to view its status history · use Update to move it along the pipeline" accent="linear-gradient(90deg,#10b981,#34d399)">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SO #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesOrders.map((o, idx) => (
                      <TableRow key={o.id} hover sx={{ cursor: 'pointer' }} onClick={() => setHistoryId(o.id)}>
                        <TableCell sx={{ fontWeight: 600 }}>{o.id}</TableCell>
                        <TableCell>{o.customer}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{o.date}</TableCell>
                        <TableCell align="right">{o.items}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(o.amount)}</TableCell>
                        <TableCell><StatusChip label={o.status} tone={statusTone[o.status]} /></TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Update status with a date">
                            <Button size="small" variant="outlined" startIcon={<History size={15} />} sx={{ textTransform: 'none' }}
                              onClick={() => setStatusIndex(idx)}>
                              Update
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
